import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import {
  Building2, Plus, Search, Edit, Trash2, MoreHorizontal, MapPin,
  Phone, Mail, Globe, CheckCircle, XCircle, Clock, Eye, Users,
  DollarSign, RefreshCw, Filter, Download
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config/api';

// Status configuration
const STATUS_CONFIG = {
  active: { label: 'Active', color: 'bg-green-500/20 text-green-400', icon: CheckCircle },
  inactive: { label: 'Inactive', color: 'bg-slate-500/20 text-slate-400', icon: XCircle },
  pending: { label: 'Pending', color: 'bg-yellow-500/20 text-yellow-400', icon: Clock }
};

export default function RestaurantsPage() {
  const { token } = useOutletContext();
  const [searchParams] = useSearchParams();
  
  const [restaurants, setRestaurants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showDialog, setShowDialog] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  
  // Form state
  const [form, setForm] = useState({
    name: '',
    description: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    state: '',
    zip_code: '',
    cuisine_type: '',
    is_active: true
  });

  const getHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const fetchRestaurants = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/company-admin/restaurants`, getHeaders());
      setRestaurants(response.data || []);
    } catch (error) {
      console.error('Failed to fetch restaurants:', error);
      toast.error(error.response?.data?.detail || 'Could not load restaurants');
      setRestaurants([]);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchRestaurants();
  }, [token, fetchRestaurants]);

  const handleSave = async () => {
    try {
      if (editingRestaurant) {
        await axios.put(`${API}/company-admin/restaurants/${editingRestaurant.id}`, form, getHeaders());
        toast.success('Restaurant updated successfully');
      } else {
        await axios.post(`${API}/company-admin/restaurants`, form, getHeaders());
        toast.success('Restaurant created successfully');
      }
      setShowDialog(false);
      resetForm();
      fetchRestaurants();
    } catch (error) {
      toast.error('Failed to save restaurant');
    }
  };

  const handleDelete = async (restaurant) => {
    if (!confirm(`Delete "${restaurant.name}"? This action cannot be undone.`)) return;

    try {
      await axios.delete(`${API}/company-admin/restaurants/${restaurant.id}`, getHeaders());
      toast.success('Restaurant deleted');
      fetchRestaurants();
    } catch (error) {
      // Surface backend's structured error (409 = has dependencies)
      const detail = error?.response?.data?.detail;
      if (error?.response?.status === 409) {
        const message = typeof detail === 'object' && detail?.message ? detail.message : 'Restaurant has related data and cannot be deleted. Deactivate it instead.';
        toast.error(message);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Failed to delete restaurant');
      }
    }
  };

  const handleToggleStatus = async (restaurant) => {
    try {
      const activate = restaurant.status !== 'active';
      await axios.put(
        `${API}/company-admin/restaurants/${restaurant.id}/status`,
        { is_active: activate },
        getHeaders()
      );
      toast.success(`Restaurant ${activate ? 'activated' : 'deactivated'}`);
      fetchRestaurants();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Failed to update status');
    }
  };

  const resetForm = () => {
    setForm({
      name: '',
      description: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      state: '',
      zip_code: '',
      cuisine_type: '',
      is_active: true
    });
    setEditingRestaurant(null);
  };

  const openEdit = (restaurant) => {
    setForm({
      name: restaurant.name || '',
      description: restaurant.description || '',
      phone: restaurant.phone || '',
      email: restaurant.email || '',
      address: restaurant.address || '',
      city: restaurant.city || '',
      state: restaurant.state || '',
      zip_code: restaurant.zip_code || '',
      cuisine_type: restaurant.cuisine_type || '',
      is_active: restaurant.status === 'active'
    });
    setEditingRestaurant(restaurant);
    setShowDialog(true);
  };

  // Filter restaurants
  const filteredRestaurants = restaurants.filter(r => {
    if (statusFilter !== 'all' && r.status !== statusFilter) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        r.name?.toLowerCase().includes(query) ||
        r.city?.toLowerCase().includes(query) ||
        r.cuisine_type?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  // Stats
  const stats = {
    total: restaurants.length,
    active: restaurants.filter(r => r.status === 'active').length,
    pending: restaurants.filter(r => r.status === 'pending').length,
    inactive: restaurants.filter(r => r.status === 'inactive').length
  };

  return (
    <div className="space-y-6" data-testid="company-restaurants">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Restaurants</h1>
          <p className="text-gray-500">Manage all restaurants on the platform</p>
        </div>
        <div className="flex items-center gap-3">
          <Button 
            variant="outline" 
            size="sm"
          >
            <Download className="h-4 w-4 mr-2" />
            Export
          </Button>
          <Button 
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
            onClick={() => { resetForm(); setShowDialog(true); }}
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Restaurant
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total</p>
                <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Active</p>
                <p className="text-2xl font-bold text-green-600">{stats.active}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-yellow-100 flex items-center justify-center">
                <Clock className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Inactive</p>
                <p className="text-2xl font-bold text-gray-500">{stats.inactive}</p>
              </div>
              <div className="h-10 w-10 rounded-full bg-gray-100 flex items-center justify-center">
                <XCircle className="h-5 w-5 text-gray-500" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="bg-white border-0 shadow-sm">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[250px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search restaurants..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            
            <Button 
              variant="outline" 
              size="icon"
              onClick={fetchRestaurants}
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Restaurants Table */}
      <Card className="bg-white border-0 shadow-sm">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Restaurant</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Cuisine</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Managers</TableHead>
                  <TableHead className="text-right">Revenue</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRestaurants.length > 0 ? (
                  filteredRestaurants.map((restaurant) => {
                    const status = STATUS_CONFIG[restaurant.status] || STATUS_CONFIG.active;
                    const StatusIcon = status.icon;
                    
                    return (
                      <TableRow key={restaurant.id}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-lg bg-blue-600 flex items-center justify-center text-white font-semibold">
                              {restaurant.name?.charAt(0)}
                            </div>
                            <div>
                              <p className="font-medium text-gray-900">{restaurant.name}</p>
                              <p className="text-sm text-gray-500">{restaurant.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-gray-600">
                            <MapPin className="h-3 w-3 text-gray-400" />
                            {restaurant.city}, {restaurant.state}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">
                            {restaurant.cuisine_type}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={restaurant.status === 'active' ? 'bg-green-100 text-green-700' : restaurant.status === 'pending' ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100 text-gray-700'}>
                            {status.label}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1 text-gray-600">
                            <Users className="h-3 w-3 text-gray-400" />
                            {restaurant.managers_count || 0}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <p className="font-semibold text-gray-900">${restaurant.total_revenue?.toLocaleString() || '0'}</p>
                          <p className="text-xs text-gray-500">{restaurant.total_orders || 0} orders</p>
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem onClick={() => openEdit(restaurant)}>
                                <Edit className="h-4 w-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleToggleStatus(restaurant)}>
                                {restaurant.status === 'active' ? (
                                  <><XCircle className="h-4 w-4 mr-2" />Deactivate</>
                                ) : (
                                  <><CheckCircle className="h-4 w-4 mr-2" />Activate</>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDelete(restaurant)} className="text-red-600">
                                <Trash2 className="h-4 w-4 mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-10 text-gray-500">
                      <Building2 className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                      <p>No restaurants found</p>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingRestaurant ? 'Edit Restaurant' : 'Add New Restaurant'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            <div>
              <Label>Restaurant Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g., Golden Dragon"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Phone</Label>
                <Input
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="555-0100"
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="contact@restaurant.com"
                />
              </div>
            </div>
            
            <div>
              <Label>Street Address</Label>
              <Input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="123 Main Street"
              />
            </div>
            
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>City</Label>
                <Input
                  value={form.city}
                  onChange={(e) => setForm({ ...form, city: e.target.value })}
                  placeholder="New York"
                />
              </div>
              <div>
                <Label>State</Label>
                <Input
                  value={form.state}
                  onChange={(e) => setForm({ ...form, state: e.target.value })}
                  placeholder="NY"
                />
              </div>
              <div>
                <Label>ZIP</Label>
                <Input
                  value={form.zip_code}
                  onChange={(e) => setForm({ ...form, zip_code: e.target.value })}
                  placeholder="10001"
                />
              </div>
            </div>
            
            <div>
              <Label>Cuisine Type</Label>
              <Input
                value={form.cuisine_type}
                onChange={(e) => setForm({ ...form, cuisine_type: e.target.value })}
                placeholder="e.g., Italian, Chinese, American"
              />
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <Label>Active Status</Label>
              <Switch
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={!form.name}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {editingRestaurant ? 'Save Changes' : 'Create Restaurant'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
